import React, { useState, useEffect } from 'react';
import { Form, Button, Spinner, Modal } from 'react-bootstrap';
import axios from 'axios';
import { showToast as toast } from '../toastManager';
import '../ChatApp.css';

// ─── Constants ──────────────────────────────────────────────────────────────

const BUILT_IN_PROMPT =
  'Provide 2 variations of this section so to improve engagement and conversion rate. ' +
  'Build with a mobile-first experience as majority of traffic is on mobile.';

// Match every <style id="sxi_<anything>_font_face">…</style>. Captures the
// whole tag so we can both strip and re-inject verbatim later.
const FONT_FACE_STYLE_RE = /<style[^>]*\bid\s*=\s*["']sxi_[^"']+_font_face["'][^>]*>[\s\S]*?<\/style>/gi;

// ─── Helpers ────────────────────────────────────────────────────────────────

function extractFontFaceStyles(html) {
  const safe = html || '';
  const fontFaceBlocks = safe.match(FONT_FACE_STYLE_RE) || [];
  const stripped = safe.replace(FONT_FACE_STYLE_RE, '');
  return { stripped, fontFaceBlocks };
}

// Trailing-slash and surrounding-whitespace differences between the LP list
// (experiments keys) and section_original_url (scraper-supplied) would
// otherwise cause false negatives in the filter below.
const normalizeUrl = (u) => (u || '').trim().replace(/\/+$/, '');

function buildVariationSrcDoc({ html, css, js, fontFaceBlocks, sectionName }) {
  const ff = (fontFaceBlocks || []).join('\n');
  const safeTitle = String(sectionName || 'Variation').replace(/[<>&"]/g, '');
  return (
    `<!DOCTYPE html><html lang="en"><head>` +
    `<meta charset="utf-8">` +
    `<meta name="viewport" content="width=device-width,initial-scale=1">` +
    `<title>${safeTitle}</title>` +
    // Font-face FIRST in <head>, above the AI's CSS — preserves the original
    // typography exactly the way the user asked.
    ff +
    `<style>${css || ''}</style>` +
    `</head><body style="margin:0;padding:0">${html || ''}` +
    (js ? `<script>${js}<\/script>` : '') +
    `</body></html>`
  );
}

// Poll a /api/ai_generation/job/:jobId until succeeded/failed. Mirrors the
// cadence used by AIAssetContentGenerator (300 ticks × 2 s ≈ 10 min).
async function pollGenerationJob(app_url, jobId) {
  for (let i = 0; i < 300; i++) {
    let job;
    try {
      const res = await axios.get(`${app_url}/api/ai_generation/job/${jobId}`, { withCredentials: true });
      job = res.data?.job;
    } catch (err) {
      if (i > 2) throw new Error('Lost connection to job status');
      await new Promise(r => setTimeout(r, 2000));
      continue;
    }
    if (!job) throw new Error('Job not found');
    if (job.status === 'succeeded') return job.result || {};
    if (job.status === 'failed') throw new Error(job.error?.message || 'Generation failed');
    await new Promise(r => setTimeout(r, 2000));
  }
  throw new Error('Generation timed out');
}

// ─── Component ──────────────────────────────────────────────────────────────

const EnhanceSections = ({ ViewedPage, shop_domain, app_url }) => {
  useEffect(() => { if (ViewedPage) ViewedPage(58); }, []);

  const [loading, setLoading] = useState(true);
  const [landingPages, setLandingPages] = useState([]);
  const [selectedLp, setSelectedLp] = useState('');
  const [visibleSections, setVisibleSections] = useState([]);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [selectedSectionIds, setSelectedSectionIds] = useState(new Set());
  const [generating, setGenerating] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [variationsBySource, setVariationsBySource] = useState({}); // { [section_id]: { source, fontFaceBlocks, variations: [v1, v2], status, error } }

  // ─── Load on mount: only the LP dropdown ──────────────────────────────────

  useEffect(() => {
    if (!shop_domain || !app_url) return;
    (async () => {
      try {
        const lpsRes = await axios.post(`${app_url}/api/firebase/get_landing_pages`, {}, { withCredentials: true });
        if (lpsRes.data?.success) setLandingPages(lpsRes.data.landing_pages || []);
      } catch (err) {
        console.error('EnhanceSections load failed', err);
        toast.error('Failed to load Enhance Sections data');
      } finally {
        setLoading(false);
      }
    })();
  }, [shop_domain, app_url]);

  // ─── Fetch the LP's sections whenever the dropdown changes ────────────────

  useEffect(() => {
    if (!selectedLp || !app_url) {
      setVisibleSections([]);
      setSelectedSectionIds(new Set());
      return;
    }
    let cancelled = false;
    setSectionsLoading(true);
    setSelectedSectionIds(new Set());
    (async () => {
      try {
        const res = await axios.post(
          `${app_url}/api/firebase/get_sections`,
          { landing_page: selectedLp },
          { withCredentials: true }
        );
        if (cancelled) return;
        if (res.data?.success) {
          // The server returns untagged sections for every LP, so filter
          // client-side to only those explicitly linked to selectedLp: either
          // tagged to it, or scraped from it.
          const lp = normalizeUrl(selectedLp);
          const filtered = (res.data.data || []).filter(s => {
            const tagged = Array.isArray(s.tagged_landing_pages) ? s.tagged_landing_pages : [];
            const taggedMatch = tagged.some(t => normalizeUrl(t) === lp);
            const urlMatch    = !!lp && normalizeUrl(s.section_original_url) === lp;
            return taggedMatch || urlMatch;
          });
          setVisibleSections(filtered);
        } else setVisibleSections([]);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch LP sections', err);
          toast.error('Failed to load sections for this landing page');
          setVisibleSections([]);
        }
      } finally {
        if (!cancelled) setSectionsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedLp, app_url]);

  // ─── Selection toggles ────────────────────────────────────────────────────

  const toggleSection = (id) => {
    setSelectedSectionIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ─── Generate ─────────────────────────────────────────────────────────────

  const generateOneVariation = async (sourceSection, strippedHtml) => {
    const startRes = await axios.post(
      `${app_url}/api/ai_generation/generate_sections`,
      {
        page: selectedLp,
        outputType: 'sections',
        mode: 'improve',
        selectedAssets: {},
        selectedSection: {
          section_id: sourceSection.section_id,
          section_name: sourceSection.section_name || sourceSection.section_id,
          url: sourceSection.url || '',
          htmlContent: strippedHtml,
        },
        userMessage: BUILT_IN_PROMPT,
        llmProvider: 'gemini',
        llmModel: 'gemini-3.1-pro-preview',
      },
      { withCredentials: true }
    );
    if (!startRes.data?.success || !startRes.data?.jobId) {
      throw new Error(startRes.data?.message || 'Server did not return a jobId');
    }
    const result = await pollGenerationJob(app_url, startRes.data.jobId);
    const sections = Array.isArray(result?.sections) ? result.sections : [];
    if (sections.length === 0) throw new Error('No section returned');
    // Hand the entire array back; the caller flattens across parallel calls so
    // every section the LLM returns ends up rendered.
    return sections;
  };

  const enhanceOneSection = async (sourceSection) => {
    const sid = sourceSection.section_id;

    // Mark loading immediately so the user sees feedback even before the HTML fetch returns.
    setVariationsBySource(prev => ({
      ...prev,
      [sid]: { source: sourceSection, fontFaceBlocks: [], variations: [], status: 'loading', error: null },
    }));

    try {
      // 1. Fetch the section's current HTML.
      const htmlRes = await axios.post(
        `${app_url}/api/firebase/get_section_html`,
        { url: sourceSection.url },
        { withCredentials: true }
      );
      const originalHtml = htmlRes.data?.data || '';
      if (!originalHtml) throw new Error('Could not fetch source section HTML');

      // 2. Strip font-face <style> blocks before sending to the LLM.
      const { stripped, fontFaceBlocks } = extractFontFaceStyles(originalHtml);

      setVariationsBySource(prev => ({
        ...prev,
        [sid]: { ...prev[sid], fontFaceBlocks },
      }));

      // 3. Fire two parallel generation calls. Each returns one section, giving us 2 variations.
      const settled = await Promise.allSettled([
        generateOneVariation(sourceSection, stripped),
        generateOneVariation(sourceSection, stripped),
      ]);

      // Flatten across both calls so every section the LLM returns is kept.
      const allSections = settled.flatMap(r => (r.status === 'fulfilled' ? r.value : []));
      const firstError  = settled.find(r => r.status === 'rejected');
      const anySuccess  = allSections.length > 0;

      setVariationsBySource(prev => ({
        ...prev,
        [sid]: {
          ...prev[sid],
          variations: allSections,
          status: anySuccess ? 'done' : 'error',
          error: anySuccess ? null : (firstError?.reason?.message || 'Both variation calls failed'),
        },
      }));
    } catch (err) {
      console.error(`Enhance failed for ${sid}:`, err);
      setVariationsBySource(prev => ({
        ...prev,
        [sid]: { ...(prev[sid] || { source: sourceSection, variations: [] }), status: 'error', error: err.message || 'Enhance failed' },
      }));
    }
  };

  const handleGenerate = async () => {
    if (!selectedLp) { toast.error('Pick a landing page first'); return; }
    if (selectedSectionIds.size === 0) { toast.error('Pick at least one section'); return; }

    setGenerating(true);
    try {
      // Process all selected sources in parallel; per-source we already do 2 parallel calls.
      const tasks = [];
      for (const sid of selectedSectionIds) {
        const source = visibleSections.find(s => s.section_id === sid);
        if (source) tasks.push(enhanceOneSection(source));
      }
      await Promise.allSettled(tasks);
    } finally {
      setGenerating(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className='main-container' id='enhance-sections-main-container'>
        <div className='main_conatiner_spinner'><Spinner animation='border' /></div>
      </div>
    );
  }

  const sourceOrder = Array.from(selectedSectionIds).filter(id => variationsBySource[id]);

  return (
    <div className='main-container' id='enhance-sections-main-container'>
      {/* Banner title card — same shape as /imported-sections */}
      <div
        className='main-title mt-4 mx-4 d-flex justify-content-between align-items-center'
        style={{
          marginBottom: '10px',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          padding: '16px 20px',
          backgroundColor: '#ffffff',
        }}
      >
        <h2 style={{ margin: 0, fontWeight: 'bold' }}>Enhance Sections</h2>
        <Button
          className='sxi_primary_blue_btn'
          onClick={() => setPickerOpen(true)}
          disabled={generating}
          style={{ padding: '8px 16px' }}
        >
          {generating
            ? <><Spinner size='sm' animation='border' /> Generating…</>
            : 'Select & Generate'}
        </Button>
      </div>

      {/* Empty state when nothing has been generated yet */}
      {sourceOrder.length === 0 && !generating && (
        <div
          className='mx-4 p-4 text-center'
          style={{ border: '1px solid #e9ecef', borderRadius: 8, backgroundColor: '#ffffff', color: '#6B7280' }}
        >
          No variations yet. Click <strong style={{ color: '#132848' }}>Select &amp; Generate</strong> to choose a landing page and the sections you want to improve.
        </div>
      )}

      {sourceOrder.length > 0 && (
        <div className='mx-3 p-2'>
          {sourceOrder.map(sid => {
            const entry = variationsBySource[sid];
            const src = entry.source;
            return (
              <div
                key={sid}
                className='sxi-existing-div-wrapper'
                style={{
                  marginBottom: '1rem',
                  border: '1px solid #e9ecef',
                  borderRadius: '8px',
                  padding: '10px',
                  backgroundColor: '#ffffff',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {entry.status === 'loading' ? (
                    <div
                      style={{
                        border: '1px solid #e9ecef',
                        borderRadius: 8,
                        background: '#ffffff',
                        overflow: 'hidden',
                      }}
                    >
                      <div style={{
                        padding: '8px 14px',
                        background: '#f8f9fa',
                        borderBottom: '1px solid #e9ecef',
                        fontSize: 13,
                      }}>
                      </div>
                      <div style={{
                        height: 200, display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        color: '#6c757d', fontSize: 13,
                      }}>
                        <Spinner animation='border' size='sm' className='me-2' />
                        Generating variations…
                      </div>
                    </div>
                  ) : entry.variations?.length > 0 ? (
                    entry.variations.map((variant, idx) => (
                      <div
                        key={`${sid}-v${idx}-${variant.section_id || 'x'}`}
                        style={{
                          border: '1px solid #e9ecef',
                          borderRadius: 8,
                          background: '#ffffff',
                          overflow: 'hidden',
                        }}
                      >
                        <div style={{
                          padding: '8px 14px',
                          background: '#f8f9fa',
                          borderBottom: '1px solid #e9ecef',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          fontSize: 13,
                        }}>
                          <strong style={{ color: 'rgb(5,44,101)' }}>Variation {idx + 1}</strong>
                          {variant?.section_type && (
                            <span className='campaign-tag-new ms-1'>{variant.section_type}</span>
                          )}
                        </div>
                        <iframe
                          srcDoc={buildVariationSrcDoc({
                            html: variant.html,
                            css: variant.css,
                            js: variant.js,
                            fontFaceBlocks: entry.fontFaceBlocks,
                            sectionName: variant.section_name || src.section_name,
                          })}
                          style={{ width: '100%', height: 520, border: 'none', display: 'block', background: '#ffffff' }}
                          sandbox='allow-scripts allow-same-origin'
                          title={`${src.section_name || src.section_id} variation ${idx + 1}`}
                        />
                      </div>
                    ))
                  ) : (
                    <div
                      style={{
                        border: '1px solid #e9ecef',
                        borderRadius: 8,
                        background: '#ffffff',
                        overflow: 'hidden',
                      }}
                    >
                      <div style={{
                        height: 200, display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        color: '#b02a37', fontSize: 13, padding: 12, textAlign: 'center',
                      }}>
                        {entry.error || 'All variation calls failed.'} Re-run Generate to retry.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Picker modal — LP dropdown, scrollable section list with iframe thumbnails, Generate */}
      <Modal
        show={pickerOpen}
        onHide={() => !generating && setPickerOpen(false)}
        size='l'
        centered
        scrollable
      >
        <Modal.Header closeButton={!generating}>
          <Modal.Title>Pick a Landing Page &amp; Sections</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Form.Group className='mb-3' controlId='enhance-sections-lp-select'>
            <Form.Label className='mb-1'>Landing Page</Form.Label>
            <Form.Select
              className='form-select form-select-sm'
              value={selectedLp}
              onChange={(e) => setSelectedLp(e.target.value)}
              disabled={generating || sectionsLoading}
              style={{ border: '1px solid #dee2e6', borderRadius: '6px', padding: '6px 12px', fontSize: '14px' }}
            >
              <option value=''>Select a landing page…</option>
              {landingPages.map(lp => <option key={lp} value={lp}>{lp}</option>)}
            </Form.Select>
          </Form.Group>

          {!selectedLp ? (
            <div className='text-muted small'>Pick a landing page to see its sections.</div>
          ) : sectionsLoading ? (
            <div className='text-center py-4'><Spinner animation='border' /></div>
          ) : visibleSections.length === 0 ? (
            <div className='text-muted small'>No sections on this landing page yet.</div>
          ) : (
            <>
              <div className='mb-2 d-flex justify-content-between align-items-center' style={{ fontSize: 13, color: '#6c757d' }}>
                <span>
                  {visibleSections.length} section{visibleSections.length > 1 ? 's' : ''} on this page — click to select.
                </span>
                {selectedSectionIds.size > 0 && (
                  <span className='campaign-tag-new'>{selectedSectionIds.size} selected</span>
                )}
              </div>

              <div style={{ maxHeight: '50vh', overflowY: 'auto', paddingRight: 4 }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                    gap: 12,
                  }}
                >
                  {visibleSections.map(section => {
                    const isSelected = selectedSectionIds.has(section.section_id);
                    return (
                      <div
                        key={section.section_id}
                        onClick={() => !generating && toggleSection(section.section_id)}
                        style={{
                          border: isSelected ? '2px solid #132848' : '1px solid #dee2e6',
                          background: isSelected ? '#eef2f6' : '#ffffff',
                          borderRadius: 8,
                          overflow: 'hidden',
                          cursor: generating ? 'default' : 'pointer',
                          position: 'relative',
                        }}
                      >
                        <div style={{ width: '100%', height: 140, overflow: 'hidden', background: '#f0f2f5', position: 'relative' }}>
                          {section.url ? (
                            <iframe
                              src={section.url}
                              title={section.section_name || section.section_id}
                              sandbox='allow-same-origin'
                              scrolling='no'
                              loading='lazy'
                              style={{
                                width: '333%',
                                height: '333%',
                                transform: 'scale(0.3)',
                                transformOrigin: '0 0',
                                border: 'none',
                                pointerEvents: 'none',
                                background: '#ffffff',
                              }}
                            />
                          ) : (
                            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9aa2ad', fontSize: 13 }}>
                              No preview available
                            </div>
                          )}
                        </div>
                        {isSelected && (
                          <div style={{
                            position: 'absolute', top: 6, right: 6,
                            background: '#132848', color: '#ffffff',
                            borderRadius: '50%', width: 22, height: 22,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 12, fontWeight: 700,
                          }}>✓</div>
                        )}
                        <div style={{ padding: '6px 8px' }}>
                          <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {section.section_name || section.section_id}
                          </div>
                          {section.section_type && (
                            <div style={{ fontSize: 10, color: '#6c757d' }}>{section.section_type}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            className='sxi_primary_blue_btn'
            onClick={() => { setPickerOpen(false); handleGenerate(); }}
            disabled={!selectedLp || selectedSectionIds.size === 0 || generating}
            style={{ padding: '8px 16px' }}
          >
            {generating
              ? <><Spinner size='sm' animation='border' /> Generating…</>
              : `Generate${selectedSectionIds.size > 0 ? ` (${selectedSectionIds.size})` : ''}`}
          </Button>
          <button className='back-button' onClick={() => setPickerOpen(false)} disabled={generating}>Cancel</button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EnhanceSections;
